import { clsx } from 'clsx'
import * as Popover from '@radix-ui/react-popover'
import { useCallback, useState } from 'react'
import { DayPicker } from 'react-day-picker'
import { Button } from './Button'
import { controlSkin, useControl } from './Field'
import { Calendar, ChevronLeft, ChevronRight } from './icons'

type Props = {
  /** ISO date, `YYYY-MM-DD`; null or '' when nothing is chosen. */
  value: string | null | undefined
  onChange: (next: string | null) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  'aria-label'?: string
}

const pad = (n: number) => String(n).padStart(2, '0')
const toIso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
/** Parses as a local date; `new Date('2026-10-09')` would read it as UTC midnight and show the day before. */
function fromIso(value: string | null | undefined): Date | undefined {
  const m = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : undefined
}

/**
 * Branded date field: an underlined trigger like Select, opening a calendar (react-day-picker in a Radix popover).
 * Inside a native <dialog> the calendar portals into the dialog so it stays in the top layer.
 */
export function DatePicker({ value, onChange, placeholder = 'Choose a date', disabled, className, 'aria-label': ariaLabel }: Props) {
  const { id, describedBy, invalid } = useControl()
  const [open, setOpen] = useState(false)
  const [container, setContainer] = useState<HTMLElement | null>(null)
  const triggerRef = useCallback((el: HTMLElement | null) => setContainer(el?.closest('dialog') ?? null), [])
  const selected = fromIso(value)
  const pick = (next: string | null) => {
    onChange(next)
    setOpen(false)
  }
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        ref={triggerRef}
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        className={clsx(
          controlSkin,
          'flex w-full cursor-pointer items-center justify-between gap-3 py-2 text-left text-body tabular-nums',
          !selected && 'text-dim',
          invalid ? 'border-danger' : 'border-rule hover:border-ink data-[state=open]:border-ink',
          className,
        )}
      >
        <span className="truncate">{selected ? selected.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : placeholder}</span>
        <Calendar size={16} className="shrink-0 text-ink" />
      </Popover.Trigger>
      <Popover.Portal container={container ?? undefined}>
        <Popover.Content
          align="start"
          sideOffset={6}
          collisionPadding={16}
          // Let the calendar put focus on the chosen day (or today) rather than the "previous month" arrow.
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="popover-menu z-50 max-w-[calc(100vw-2rem)] border border-ink bg-paper p-3 text-ink shadow-[0_12px_32px_rgb(0_0_0/0.14)] sm:p-4"
        >
          <DayPicker
            mode="single"
            selected={selected}
            defaultMonth={selected}
            onSelect={(d) => pick(d ? toIso(d) : null)}
            autoFocus
            showOutsideDays
            fixedWeeks
            components={{ Chevron: ({ orientation }) => (orientation === 'left' ? <ChevronLeft size={16} /> : <ChevronRight size={16} />) }}
            classNames={{
              root: 'relative',
              month: 'grid gap-2',
              month_caption: 'flex h-9 items-center pr-20',
              caption_label: 'font-display text-h5 text-ink',
              nav: 'absolute right-0 top-0 flex',
              button_previous: 'grid h-9 w-9 cursor-pointer place-items-center text-ink transition-colors hover:bg-gray-100 disabled:cursor-default disabled:text-gray-400',
              button_next: 'grid h-9 w-9 cursor-pointer place-items-center text-ink transition-colors hover:bg-gray-100 disabled:cursor-default disabled:text-gray-400',
              month_grid: 'border-collapse',
              weekday: 'h-8 w-9 text-[0.6875rem] sm:w-10 font-medium uppercase tracking-eyebrow text-dim',
              day: 'p-0 text-center',
              day_button: 'grid h-9 w-9 cursor-pointer sm:h-10 sm:w-10 place-items-center text-small tabular-nums transition-colors hover:bg-gray-100',
              today: 'font-semibold [&>button]:underline [&>button]:decoration-gold [&>button]:decoration-2 [&>button]:underline-offset-4',
              selected: '[&>button]:bg-ink [&>button]:text-paper [&>button]:hover:bg-ink',
              outside: 'text-gray-400',
              disabled: 'text-gray-400 [&>button]:cursor-not-allowed [&>button]:hover:bg-transparent',
              hidden: 'invisible',
            }}
          />
          <div className="mt-3 flex items-center justify-between border-t border-gray-200 pt-3">
            <Button variant="link" size="sm" arrow={false} onClick={() => pick(toIso(new Date()))}>
              Today
            </Button>
            {selected && (
              <Button variant="danger-link" size="sm" arrow={false} onClick={() => pick(null)}>
                Clear
              </Button>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
